import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Label,
} from '@asad/crane-ui'

import type { MapDevice } from '../types'

type DevicePickerModalProps = {
  open: boolean
  devices: MapDevice[]
  selectedIds: string[]
  onApply: (ids: string[]) => void
  onCancel: () => void
}

export const DevicePickerModal = ({ open, devices, selectedIds, onApply, onCancel }: DevicePickerModalProps) => {
  const { t } = useTranslation()
  const [selection, setSelection] = React.useState<string[]>(selectedIds)

  React.useEffect(() => {
    if (open) setSelection(selectedIds)
  }, [open, selectedIds])

  const toggle = (id: string, checked: boolean) =>
    setSelection((current) => (checked ? [...current, id] : current.filter((item) => item !== id)))

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel()
      }}
    >
      <DialogContent className='max-h-crane-modal max-w-xl grid-rows-crane-modal'>
        <DialogHeader>
          <DialogTitle>{t('dashboardViews.manageDevices')}</DialogTitle>
          <DialogDescription>{t('dashboardViews.manageDevicesHint')}</DialogDescription>
        </DialogHeader>
        <div className='overflow-y-auto rounded-crane-md bg-crane-card-inset p-2'>
          {devices.length === 0 ? <EmptyState title={t('dashboardViews.noAvailableDevices')} /> : null}
          {devices.map((device) => {
            const id = `dashboard-device-${device.id}`
            return (
              <Label
                key={device.id}
                htmlFor={id}
                className='flex cursor-pointer items-center gap-3 rounded-crane-sm px-2.5 py-2 hover:bg-crane-card'
              >
                <Checkbox
                  id={id}
                  checked={selection.includes(device.id)}
                  onCheckedChange={(value) => toggle(device.id, value === true)}
                />
                <span className='min-w-0'>
                  <span className='block truncate text-sm font-semibold'>{device.name}</span>
                  <span className='block truncate text-xs font-normal text-crane-muted-foreground'>
                    {device.serialNumber}
                  </span>
                </span>
              </Label>
            )
          })}
        </div>
        <DialogFooter className='gap-2'>
          <span className='mr-auto self-center text-xs text-crane-muted-foreground'>
            {t('dashboardViews.selectedCount', { count: selection.length })}
          </span>
          <Button variant='ghost' onClick={onCancel}>
            {t('commonActions.cancel')}
          </Button>
          <Button disabled={!selection.length} onClick={() => onApply(selection)}>
            {t('commonActions.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
